import { Badge, CollapsibleSection } from '@civilgrid/ui/components';
import {
  FEATURE_DEFAULTS,
  type FeatureBillingTier,
  isBillingTierEnabled,
  isFeatureConfigurable,
  resolveFeatureMatrix,
} from '@civilgrid/utils/src/common/featureAccess';
import classNames from 'classnames';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';
import { useState } from 'react';
import type { Subscription, SubscriptionDetail } from '~/common/types';
import { FEATURE_META } from './featureMeta';

function formatSubscriptionDate(value?: Date | string | null) {
  if (!value) return '—';

  const date = typeof value === 'string' ? DateTime.fromISO(value) : DateTime.fromJSDate(value);
  return date.isValid ? date.toLocaleString(DateTime.DATETIME_MED) : '—';
}

function TierStatusBadge({ subscription, tier }: { subscription: Subscription; tier: FeatureBillingTier }) {
  const enabled = isBillingTierEnabled(tier, subscription);
  const testId = `sub-tier-status-${tier}`;
  return enabled ? (
    <Badge color="green" data-testid={testId}>
      Enabled
    </Badge>
  ) : (
    <Badge kind="outline" color="grey" data-testid={testId}>
      Disabled
    </Badge>
  );
}

/**
 * Wrapped feature chips for a tier. Fill = enabled (green) / disabled (grey); a yellow outline marks a
 * feature whose enabled state for this tier deviates from the code default (an org override).
 * Non-configurable features (CivilSearch/Digitization) render dark-grey for reference only — their
 * availability is hardcoded and never overridden.
 */
function TierFeatureChips({
  matrix,
  tier,
}: {
  matrix: ReturnType<typeof resolveFeatureMatrix>;
  tier: FeatureBillingTier;
}) {
  return (
    <div className="mt-2 flex flex-wrap gap-1" data-testid={`sub-feature-chips-${tier}`}>
      {matrix.map((entry) => {
        const configurable = isFeatureConfigurable(entry.feature);
        const on = entry.tiers.includes(tier);
        const isTierOverride = configurable && on !== FEATURE_DEFAULTS[entry.feature].includes(tier);
        const label = FEATURE_META[entry.feature].label;
        const testId = `sub-feature-${tier}-${entry.feature}`;
        const color = configurable && on ? 'green' : 'grey';
        return (
          <Badge
            key={entry.feature}
            kind="outline"
            color={color}
            data-testid={testId}
            className={classNames({ 'bg-grey-20': !configurable, 'border-yellow-60': isTierOverride })}
          >
            {label}
          </Badge>
        );
      })}
    </div>
  );
}

function SubscriptionPricingDisplay({ subscription }: { subscription: Subscription }) {
  const matrix = resolveFeatureMatrix(subscription);
  return (
    <div className="space-y-4">
      <div className="text-xs space-y-1">
        <div>SFDC Account ID: {subscription.sfdcAccountId ?? '—'}</div>
        <div>SFDC Opportunity ID: {subscription.sfdcOpportunityId ?? '—'}</div>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          <div className="text-sm font-bold">Drafts</div>
          <TierStatusBadge subscription={subscription} tier="draft" />
        </div>
        <TierFeatureChips matrix={matrix} tier="draft" />
      </div>

      <hr />

      <div>
        <div className="mb-2 flex items-center gap-2">
          <div className="text-sm font-bold">Project Lite Pricing</div>
          <TierStatusBadge subscription={subscription} tier="proposal" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <h5 className="font-medium mb-1">Polygon</h5>
            <div className="text-xs space-y-1">
              <div>
                Urban: {subscription.projectCost.proposal.polygon.urban.unit} acres @ $
                {(subscription.projectCost.proposal.polygon.urban.cost / 100).toFixed(2)}
              </div>
              <div>
                Rural: {subscription.projectCost.proposal.polygon.rural.unit} acres @ $
                {(subscription.projectCost.proposal.polygon.rural.cost / 100).toFixed(2)}
              </div>
            </div>
          </div>
          <div>
            <h5 className="font-medium mb-1">Linear</h5>
            <div className="text-xs space-y-1">
              <div>
                Urban: {subscription.projectCost.proposal.linear.urban.unit} miles @ $
                {(subscription.projectCost.proposal.linear.urban.cost / 100).toFixed(2)}
              </div>
              <div>
                Rural: {subscription.projectCost.proposal.linear.rural.unit} miles @ $
                {(subscription.projectCost.proposal.linear.rural.cost / 100).toFixed(2)}
              </div>
            </div>
          </div>
          <div>
            <h5 className="font-medium mb-1">Circle</h5>
            <div className="text-xs space-y-1">
              <div>
                Urban: {subscription.projectCost.proposal.circle.urban.unit} acres @ $
                {(subscription.projectCost.proposal.circle.urban.cost / 100).toFixed(2)}
              </div>
              <div>
                Rural: {subscription.projectCost.proposal.circle.rural.unit} acres @ $
                {(subscription.projectCost.proposal.circle.rural.cost / 100).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
        <TierFeatureChips matrix={matrix} tier="proposal" />
      </div>

      <hr />

      <div>
        <div className="mb-2 flex items-center gap-2">
          <div className="text-sm font-bold">Project Pricing</div>
          <TierStatusBadge subscription={subscription} tier="project" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <h5 className="font-medium mb-1">Polygon</h5>
            <div className="text-xs space-y-1">
              <div>
                Urban: {subscription.projectCost.project.polygon.urban.unit} acres @ $
                {(subscription.projectCost.project.polygon.urban.cost / 100).toFixed(2)}
              </div>
              <div>
                Rural: {subscription.projectCost.project.polygon.rural.unit} acres @ $
                {(subscription.projectCost.project.polygon.rural.cost / 100).toFixed(2)}
              </div>
            </div>
          </div>
          <div>
            <h5 className="font-medium mb-1">Linear</h5>
            <div className="text-xs space-y-1">
              <div>
                Urban: {subscription.projectCost.project.linear.urban.unit} miles @ $
                {(subscription.projectCost.project.linear.urban.cost / 100).toFixed(2)}
              </div>
              <div>
                Rural: {subscription.projectCost.project.linear.rural.unit} miles @ $
                {(subscription.projectCost.project.linear.rural.cost / 100).toFixed(2)}
              </div>
            </div>
          </div>
          <div>
            <h5 className="font-medium mb-1">Circle</h5>
            <div className="text-xs space-y-1">
              <div>
                Urban: {subscription.projectCost.project.circle.urban.unit} acres @ $
                {(subscription.projectCost.project.circle.urban.cost / 100).toFixed(2)}
              </div>
              <div>
                Rural: {subscription.projectCost.project.circle.rural.unit} acres @ $
                {(subscription.projectCost.project.circle.rural.cost / 100).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
        <TierFeatureChips matrix={matrix} tier="project" />
      </div>

      <hr />

      <div>
        <div className="mb-2 flex items-center gap-2">
          <div className="text-sm font-bold">Project Plus Pricing</div>
          <TierStatusBadge subscription={subscription} tier="projectPlus" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <h5 className="font-medium mb-1">Polygon</h5>
            <div className="text-xs space-y-1">
              <div>
                Urban: {subscription.projectCost.projectPlus.polygon.urban.unit} acres @ $
                {(subscription.projectCost.projectPlus.polygon.urban.cost / 100).toFixed(2)}
              </div>
              <div>
                Rural: {subscription.projectCost.projectPlus.polygon.rural.unit} acres @ $
                {(subscription.projectCost.projectPlus.polygon.rural.cost / 100).toFixed(2)}
              </div>
            </div>
          </div>
          <div>
            <h5 className="font-medium mb-1">Linear</h5>
            <div className="text-xs space-y-1">
              <div>
                Urban: {subscription.projectCost.projectPlus.linear.urban.unit} miles @ $
                {(subscription.projectCost.projectPlus.linear.urban.cost / 100).toFixed(2)}
              </div>
              <div>
                Rural: {subscription.projectCost.projectPlus.linear.rural.unit} miles @ $
                {(subscription.projectCost.projectPlus.linear.rural.cost / 100).toFixed(2)}
              </div>
            </div>
          </div>
          <div>
            <h5 className="font-medium mb-1">Circle</h5>
            <div className="text-xs space-y-1">
              <div>
                Urban: {subscription.projectCost.projectPlus.circle.urban.unit} acres @ $
                {(subscription.projectCost.projectPlus.circle.urban.cost / 100).toFixed(2)}
              </div>
              <div>
                Rural: {subscription.projectCost.projectPlus.circle.rural.unit} acres @ $
                {(subscription.projectCost.projectPlus.circle.rural.cost / 100).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
        <TierFeatureChips matrix={matrix} tier="projectPlus" />
      </div>
    </div>
  );
}

function formatUserLabel(user?: { name: string; email: string } | null, userId?: string | null) {
  if (user) return `${user.name} (${user.email})`;
  return userId ?? '—';
}

function SubscriptionDetailsView({ subscription }: { subscription: SubscriptionDetail }) {
  const createdByLabel = formatUserLabel(subscription.createdBy, subscription.createdById);
  const updatedByLabel = formatUserLabel(subscription.updatedBy, subscription.updatedById);

  return (
    <div className="space-y-3 border-l border-grey-30 pl-4 ml-2 py-2">
      <div className="text-xs space-y-1">
        <div>Status: {capitalize(subscription.status)}</div>
        <div>Zoning access: {subscription.zoningAccess}</div>
        <div>Zoning cost: ${(subscription.zoningCost / 100).toFixed(2)}</div>
        <div>Created by: {createdByLabel}</div>
        <div>Updated by: {updatedByLabel}</div>
      </div>
      <SubscriptionPricingDisplay subscription={subscription} />
    </div>
  );
}

function SubscriptionHistoryItem({ subscription }: { subscription: SubscriptionDetail }) {
  const [open, setOpen] = useState(false);
  const createdByLabel = formatUserLabel(subscription.createdBy, subscription.createdById);
  const updatedByLabel = formatUserLabel(subscription.updatedBy, subscription.updatedById);
  const title = [
    `${subscription.id}`,
    `Start: ${formatSubscriptionDate(subscription.startDate)}`,
    `End: ${formatSubscriptionDate(subscription.endDate)}`,
    `Created by: ${createdByLabel}`,
    `Updated by: ${updatedByLabel}`,
  ].join(' · ');

  return (
    <div className={'border border-grey-30 rounded-sm py-2'}>
      <CollapsibleSection
        title={title}
        open={open}
        badge={subscription.status === 'active' ? <Badge color="green">Active</Badge> : null}
        onOpenChange={setOpen}
        size="compact"
        toggleTestId={`subscription-history-item-${subscription.id}`}
        className="border-b-0"
      >
        <div className="px-2 pb-2">
          <SubscriptionDetailsView subscription={subscription} />
        </div>
      </CollapsibleSection>
    </div>
  );
}

export function SubscriptionHistoryList({ subscriptions }: { subscriptions: SubscriptionDetail[] }) {
  if (subscriptions.length === 0) {
    return <div className="text-gray-500 italic">No subscriptions found for this organization.</div>;
  }

  return (
    <div className="space-y-2">
      {subscriptions.map((subscription) => (
        <SubscriptionHistoryItem key={subscription.id} subscription={subscription} />
      ))}
    </div>
  );
}
